import { format, differenceInCalendarDays, addDays } from 'date-fns';
import {
  useEffect,
  useCallback,
} from 'react';

import { useWidgetState } from './use-widget-state';
/**
 * @typedef   {Object}    CustomTabState  a map of state and state update functions
 *                                        that are needed to control tab values and actions
 * @property  {Object?}   tabState        an object containing the tab fields' current values
 * @property  {string?}   buttonLink     a URL query string to apply to external tab links
 * @property  {Function}  setTabState     a function to update the tab fields' values
 */

/**
 * A custom hook for `Lodging` tab type (handling specifically Stayntouch)
 * @returns {CustomTabState} a map of state and state update functions for all `Lodging` tabs
 */
export const useStayntouchLodging = ({
  commerceEngineUrl,
  lodgingDepartureOffest,
  promoCode,
}) => {
  const {
    tabValues,
    setTabValues,
    buttonLink,
    setButtonLink,
  } = useWidgetState();
  const components = [
    {
      name: 'checkin',
      ariaLabel: 'Select check-in date',
      label: 'Check-in',
      type: 'datepicker',
      isDefaultDate: false,
      isCurrentDaySelectable: true,
      placeholder: 'MM-DD-YYYY',
      maxDaysToBookAhead: 550,
      prettyDate: 'MMM dd yyyy',
      dateFormat: 'MM-dd-yyyy',
    },
    {
      name: 'checkout',
      ariaLabel: 'Select check-out date',
      label: 'Check-out',
      type: 'datepicker',
      isDefaultDate: false,
      isCurrentDaySelectable: false,
      placeholder: 'MM-DD-YYYY',
      maxDaysToBookAhead: 550,
      prettyDate: 'MMM dd yyyy',
      dateFormat: 'MM-dd-yyyy',
    },
    {
      name: 'adults',
      ariaLabel: 'Select number of adult guests',
      label: 'Adult Guests',
      type: 'select',
      placeholder: 'Adults',
      defaultValue: { value: 1, label: '1 Adult' },
      choices: [
        { value: 0, label: 'No Adults' },
        { value: 1, label: '1 Adult' },
        { value: 2, label: '2 Adults' },
        { value: 3, label: '3 Adults' },
        { value: 4, label: '4 Adults' },
        { value: 5, label: '5 Adults' },
        { value: 6, label: '6 Adults' },
        { value: 7, label: '7 Adults' },
        { value: 8, label: '8 Adults' },
        { value: 9, label: '9 Adults' },
      ],
    },
    {
      name: 'children',
      ariaLabel: 'Select number of child guests',
      label: 'Child Guests',
      type: 'select',
      placeholder: 'Children',
      defaultValue: { value: 0, label: 'No Children' },
      choices: [
        { value: 0, label: 'No Children' },
        { value: 1, label: '1 Child' },
        { value: 2, label: '2 Children' },
        { value: 3, label: '3 Children' },
        { value: 4, label: '4 Children' },
        { value: 5, label: '5 Children' },
        { value: 6, label: '6 Children' },
        { value: 7, label: '7 Children' },
        { value: 8, label: '8 Children' },
        { value: 9, label: '9 Children' },
      ],
    },
  ];

  useEffect(() => {
    const queries = [];

    if (tabValues[components[0].name]) {
      const { selectedDate, minDate } = tabValues[components[0].name];
      queries.push(
        `${components[0].name}=${encodeURIComponent(
          format(selectedDate || minDate, 'MM-dd-yyyy'),
        )}`,
      );
    }

    if (tabValues[components[0].name] && tabValues[components[1].name]) {
      const { selectedDate, minDate } = tabValues[components[1].name];
      queries.push(
        `${components[1].name}=${encodeURIComponent(
          format(selectedDate || minDate, 'MM-dd-yyyy'),
        )}`,
      );
    }

    if (tabValues[components[2].name]) {
      queries.push(`${components[2].name}=${tabValues[components[2].name].value}`);
    }

    if (tabValues[components[3].name]) {
      queries.push(`${components[3].name}=${tabValues[components[3].name].value}`);
    }

    if (promoCode) {
      queries.push(`promotionCode=${promoCode}`);
    }

    if (
      tabValues[components[0].name]
      && tabValues[components[1].name]
      && tabValues[components[2].name]
    ) {
      const updatedQueryString = queries.join('&');
      setButtonLink(`${commerceEngineUrl}?${updatedQueryString}`);
    }
  }, [tabValues]);

  const customSetTabValues = useCallback((update) => {
    const updatedTabValues = { ...update };

    // Set the min date of the departureDate field to the selected date of the arrivalDate field
    if (updatedTabValues[components[0].name]?.selectedDate) {
      updatedTabValues[components[1].name] = {
        ...(tabValues[components[1].name] ?? {}),
        isAutoSelected: true,
        minDate: addDays(
          updatedTabValues[components[0].name].selectedDate,
          lodgingDepartureOffest || 1,
        ),
      };
    }

    setTabValues(updatedTabValues);
  }, [tabValues]);

  if (tabValues[components[2].name] === undefined) {
    tabValues[components[2].name] = {
      ...components[2].choices[1],
    };
  }

  return {
    tabValues,
    setTabValues: customSetTabValues,
    buttonLink,
    components,
  };
};
